import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import PmgAssignmentSection from "../product-management-groups/PmgAssignmentSection";
import type { Link, EntityStatus } from "../../types";
import { EntityType } from "../../types";
import { linkToEditDraft } from "../../utils/linkFields";

interface EditLinkDialogProps {
  link: Link | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: Link) => void;
}

const EditLinkDialog: React.FC<EditLinkDialogProps> = ({
  link,
  open,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Link | null>(link ? linkToEditDraft(link) : null);
  const [saving, setSaving] = useState(false);
  const [pmgAccessDenied, setPmgAccessDenied] = useState(false);
  const readOnly = pmgAccessDenied;

  useEffect(() => {
    setDraft(link ? linkToEditDraft(link) : null);
  }, [link]);

  const handleSave = async () => {
    if (!draft?.uuid) return;
    setSaving(true);
    try {
      const updated = await apiService.updateLink(draft.uuid, {
        name: draft.name,
        entityClass: draft.entityClass?.trim() || undefined,
        description: draft.description?.trim() || undefined,
        status: draft.status,
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update link");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Edit Link</DialogTitle>
      <DialogContent>
        {draft && (
          <>
            <TextField
              fullWidth
              label="Name"
              margin="normal"
              value={draft.name}
              disabled={readOnly}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="URL"
              margin="normal"
              value={draft.entityClass ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                setDraft({ ...draft, entityClass: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Description"
              margin="normal"
              multiline
              minRows={2}
              value={draft.description ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                setDraft({ ...draft, description: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Tenant ID"
              margin="normal"
              value={draft.tenantId}
              disabled
            />
            <TextField
              select
              fullWidth
              label="Status"
              margin="normal"
              value={draft.status}
              disabled={readOnly}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  status: e.target.value as EntityStatus,
                })
              }
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
            <PmgAssignmentSection
              entityUuid={draft.uuid}
              entityType={EntityType.LINK}
              onAccessDenied={setPmgAccessDenied}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || pmgAccessDenied}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLinkDialog;
